/**
 * coverGeometry — where an image lands under object-fit: cover, and how far
 * it can still move (Plan G).
 *
 * Under contain the image sits inside the box and Ken Burns may only pan as
 * far as the zoom's slack. Under cover the image is LARGER than the box on
 * one axis: the crop itself is an offset we choose (put the face as near the
 * centre as the overflow allows), and whatever overflow is left on each side
 * is extra room the pan may use. All lengths in CSS px of the box; targets
 * and results that are fractions are 0–1.
 *
 * Returns null when any dimension is unknown.
 */
export const KB_DEAD_ZONE = 0.06;

export function coverGeometry(containerW, containerH, naturalW, naturalH, targetX = 0.5, targetY = 0.4) {
  if (!containerW || !containerH || !naturalW || !naturalH) return null;
  const scale = Math.max(containerW / naturalW, containerH / naturalH);
  const displayedW = naturalW * scale;
  const displayedH = naturalH * scale;
  const overflowX = Math.max(0, displayedW - containerW);
  const overflowY = Math.max(0, displayedH - containerH);

  // Offset that would put the target at the box's centre, clamped so no
  // edge of the box is left uncovered.
  const wantX = containerW / 2 - targetX * displayedW;
  const wantY = containerH / 2 - targetY * displayedH;
  const offsetX = Math.min(0, Math.max(-overflowX, wantX)) || 0;   // || 0: never -0
  const offsetY = Math.min(0, Math.max(-overflowY, wantY)) || 0;

  // Where the target ended up in the box, 0–1 — the "face position" the pan
  // reasons about (after the crop, not before).
  const frameX = (offsetX + targetX * displayedW) / containerW;
  const frameY = (offsetY + targetY * displayedH) / containerH;

  // Room left to slide the image, as fractions of the IMAGE's own size (what
  // CSS translate % uses): moving right reveals the hidden left strip, and
  // vice versa.
  const marginRight = (-offsetX / displayedW) || 0;
  const marginLeft  = ((overflowX + offsetX) / displayedW) || 0;
  const marginDown  = (-offsetY / displayedH) || 0;
  const marginUp    = ((overflowY + offsetY) / displayedH) || 0;

  return { containerW, containerH, displayedW, displayedH, offsetX, offsetY, frameX, frameY,
           margins: { left: marginLeft, right: marginRight, up: marginUp, down: marginDown } };
}

/**
 * planKenBurnsCover — the pan for a cover-cropped image.
 *
 * Same intent as planKenBurns (the thread's contain version): aim the drift
 * at the face, clamp the pan as a vector so the trajectory keeps its bearing,
 * hold still when the face is already centred or there is no zoom. The
 * difference is the bound per axis: the zoom's slack PLUS the crop's margin
 * on that side. tx/ty are fractions of the image element's own size, as
 * translate(%) expects. Positive tx moves the image right (brings a face on
 * the left toward the centre).
 */
export function planKenBurnsCover(zoomAmount, geo, hasTarget = true) {
  if (!geo) return { still: true, zoom: 1, tx: 0, ty: 0 };
  const dxFrame = 0.5 - geo.frameX;
  const dyFrame = 0.5 - geo.frameY;
  if (hasTarget && Math.abs(dxFrame) < KB_DEAD_ZONE && Math.abs(dyFrame) < KB_DEAD_ZONE) {
    return { still: true, zoom: 1, tx: 0, ty: 0 };
  }
  if (zoomAmount <= 1) return { still: true, zoom: 1, tx: 0, ty: 0 };

  // Wanted move in box units, converted to element units for translate(%).
  const wantX = (dxFrame * zoomAmount) * (geo.containerW / geo.displayedW);
  const wantY = (dyFrame * zoomAmount) * (geo.containerH / geo.displayedH);

  const slack = (zoomAmount - 1) / 2;
  const boundX = wantX >= 0 ? slack + geo.margins.right : slack + geo.margins.left;
  const boundY = wantY >= 0 ? slack + geo.margins.down  : slack + geo.margins.up;

  let k = 1;
  if (Math.abs(wantX) > boundX) k = Math.min(k, boundX / Math.abs(wantX));
  if (Math.abs(wantY) > boundY) k = Math.min(k, boundY / Math.abs(wantY));
  if (!Number.isFinite(k)) k = 0;

  return { still: false, zoom: zoomAmount, tx: wantX * k, ty: wantY * k };
}
